import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import api from '@/hooks/useApi';

// ============================================================================
// Types
// ============================================================================

export interface TokenBalance {
  balance: number;
  purchasedTokens: number;
  subscriptionTokens: number;
  totalGranted: number;
  totalSpent: number;
}

export interface TokenTransaction {
  transactionId: string;
  type: 'grant' | 'spend' | 'refund';
  amount: number;
  balanceAfter: number;
  description: string;
  createdAt: string;
  metadata?: {
    nodeId?: string;
    storyId?: string;
    source?: string;
  };
}

interface TransactionHistoryResponse {
  transactions: TokenTransaction[];
  nextCursor?: string;
}

// ============================================================================
// Query Keys
// ============================================================================

export const tokenKeys = {
  all: ['tokens'] as const,
  balance: () => [...tokenKeys.all, 'balance'] as const,
  history: () => [...tokenKeys.all, 'history'] as const,
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Fetch current user's token balance
 */
export function useTokenBalance() {
  return useQuery({
    queryKey: tokenKeys.balance(),
    queryFn: async () => {
      const response = await api.get<TokenBalance>('/tokens/balance');
      return response.data;
    },
    // Ensure balance is refreshed when the user returns to the Profile
    // or focuses the window, even if the data is considered "fresh".
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });
}

/**
 * Fetch paginated transaction history
 */
export function useTokenHistory(limit = 20) {
  return useInfiniteQuery({
    queryKey: tokenKeys.history(),
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      params.set('limit', String(limit));
      if (pageParam) {
        params.set('cursor', pageParam);
      }
      const response = await api.get<TransactionHistoryResponse>(
        `/tokens/history?${params.toString()}`
      );
      return response.data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });
}
